import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { OrderSlipReportRow, Pageable, OrderSlipReportParams as OrderSlipReportParamsType } from "@/interface";
import {
  OrderSlip,
  OrderSaleSlipReportRow,
  OrderSaleSlipReportParams,
  OrderSlipReportSearchDto,
  SaleSlipReportSearchDto,
} from "@/interface/dispatch/dp_order_slip";

export const orderSlipQueryKey = "orderSlip";

/**
 * 주문 전표 리포트 조회 (Paginated)
 * OrderSlipReportSearchDto를 사용하여 검색 조건 전달
 */
export const useOrderSlipReport = (params: OrderSlipReportParamsType & { searchDto?: OrderSlipReportSearchDto }) => {
  const { page = 0, size = 20, sort, dateFrom, dateTo, searchDto } = params;

  return useQuery<Pageable<OrderSlipReportRow>>({
    queryKey: [orderSlipQueryKey, "report", page, size, sort, dateFrom, dateTo, searchDto],
    queryFn: () => {
      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("size", size.toString());
      queryParams.append("dateFrom", dateFrom);
      queryParams.append("dateTo", dateTo);
      if (sort) queryParams.append("sort", sort);

      // OrderSlipReportSearchDto 필드들을 쿼리 파라미터로 추가
      if (searchDto) {
        if (searchDto.companyCode) queryParams.append("companyCode", searchDto.companyCode);
        if (searchDto.custName) queryParams.append("custName", searchDto.custName);
        if (searchDto.carNumber) queryParams.append("carNumber", searchDto.carNumber);
        if (searchDto.driverName) queryParams.append("driverName", searchDto.driverName);
        if (searchDto.driverTel) queryParams.append("driverTel", searchDto.driverTel);
        if (searchDto.inLocation) queryParams.append("inLocation", searchDto.inLocation);
        if (searchDto.outLocation) queryParams.append("outLocation", searchDto.outLocation);
        if (searchDto.outArea1) queryParams.append("outArea1", searchDto.outArea1);
        if (searchDto.outArea2) queryParams.append("outArea2", searchDto.outArea2);
        if (searchDto.dispatchStaffName) queryParams.append("dispatchStaffName", searchDto.dispatchStaffName);
        if (searchDto.itemName) queryParams.append("itemName", searchDto.itemName);
        if (searchDto.delYn) queryParams.append("delYn", searchDto.delYn);
        if (searchDto.exactMatch !== undefined) queryParams.append("exactMatch", searchDto.exactMatch.toString());
      }

      return api.get(`/order-slip/report?${queryParams.toString()}`);
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

/**
 * 주문 전표 단일 조회 (report-one)
 */
export const useOrderSlipReportOne = (orderSlipCode: string, options?: { enabled?: boolean; delYn?: string }) => {
  const { enabled = true, delYn = "N" } = options || {};

  return useQuery<OrderSlipReportRow>({
    queryKey: [orderSlipQueryKey, "report-one", orderSlipCode, delYn],
    queryFn: () => {
      const queryParams = new URLSearchParams();
      queryParams.append("orderSlipCode", orderSlipCode);
      queryParams.append("delYn", delYn);

      return api.get(`/order-slip/report-one?${queryParams.toString()}`);
    },
    enabled: enabled && !!orderSlipCode,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

/**
 * 주문 전표 매출 전표 리포트 조회 (Paginated)
 * SaleSlipReportSearchDto를 사용하여 검색 조건 전달
 */
export const useOrderSaleSlipReport = (params: OrderSaleSlipReportParams) => {
  const { page = 0, size = 20, sort, dateFrom, dateTo, searchDto } = params;

  return useQuery<Pageable<OrderSaleSlipReportRow>>({
    queryKey: ["orderSaleSlipReport", page, size, sort, dateFrom, dateTo, searchDto],
    queryFn: () => {
      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("size", size.toString());
      queryParams.append("dateFrom", dateFrom);
      queryParams.append("dateTo", dateTo);
      if (sort) queryParams.append("sort", sort);

      // SaleSlipReportSearchDto 필드들을 쿼리 파라미터로 추가
      if (searchDto) {
        if (searchDto.custName) queryParams.append("custName", searchDto.custName);
        if (searchDto.carNumber) queryParams.append("carNumber", searchDto.carNumber);
        if (searchDto.driverName) queryParams.append("driverName", searchDto.driverName);
        if (searchDto.driverTel) queryParams.append("driverTel", searchDto.driverTel);
        if (searchDto.inLocation) queryParams.append("inLocation", searchDto.inLocation);
        if (searchDto.outLocation) queryParams.append("outLocation", searchDto.outLocation);
        if (searchDto.outArea1) queryParams.append("outArea1", searchDto.outArea1);
        if (searchDto.outArea2) queryParams.append("outArea2", searchDto.outArea2);
        if (searchDto.dispatchStaffName) queryParams.append("dispatchStaffName", searchDto.dispatchStaffName);
        if (searchDto.itemName) queryParams.append("itemName", searchDto.itemName);
        if (searchDto.delYn) queryParams.append("delYn", searchDto.delYn);
        if (searchDto.exactMatch !== undefined) queryParams.append("exactMatch", searchDto.exactMatch.toString());
      }

      return api.get(`/order-slip/sale-slip-report?${queryParams.toString()}`);
    },
    enabled: !!dateFrom && !!dateTo, // dateFrom과 dateTo가 있을 때만 실행
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

/**
 * 주문 전표 추가 또는 수정
 * orderSlipCode가 있으면 수정, 없으면 추가
 */
export const useInsertAndUpdateOrderSlip = () => {
  return useMutation({
    mutationFn: (orderSlip: OrderSlip) => {
      return api.post("/order-slip", orderSlip);
    },
    onSuccess: () => {},
  });
};

/**
 * 주문 전표 소프트 삭제 (복수 선택 가능, delYn을 'Y'로 변경)
 */
export const useDeleteOrderSlips = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["deleteOrderSlips"],
    mutationFn: ({ orderSlipCodes, delId }: { orderSlipCodes: string[]; delId: string }) => {
      return api.delete("/order-slip/soft", {
        data: orderSlipCodes,
        params: { delId },
      });
    },
    onSuccess: () => {
      // 성공 시 orderSlip 쿼리 무효화하여 리스트 새로고침
      queryClient.invalidateQueries({ queryKey: [orderSlipQueryKey] });
    },
  });
};

/**
 * 주문 전표 하드 삭제 (복수 선택 가능, 완전 삭제)
 */
export const useHardDeleteOrderSlips = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["hardDeleteOrderSlips"],
    mutationFn: (orderSlipCodes: string[]) => {
      return api.delete("/order-slip/hard", {
        data: orderSlipCodes,
      });
    },
    onSuccess: () => {
      // 성공 시 orderSlip 쿼리 무효화하여 리스트 새로고침
      queryClient.invalidateQueries({ queryKey: [orderSlipQueryKey] });
    },
  });
};

/**
 * 주문 전표 복구 (복수 선택 가능, delYn을 'N'으로 변경)
 */
export const useRestoreOrderSlips = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["restoreOrderSlips"],
    mutationFn: ({ orderSlipCodes, delId }: { orderSlipCodes: string[]; delId: string }) => {
      return api.post("/order-slip/restore", orderSlipCodes, {
        params: { delId },
      });
    },
    onSuccess: () => {
      // 성공 시 orderSlip 쿼리 무효화하여 리스트 새로고침
      queryClient.invalidateQueries({ queryKey: [orderSlipQueryKey] });
    },
  });
};

/**
 * 주문 전표 Excel 업로드
 */
export interface ExcelUploadResponse {
  success: boolean;
  message: string;
  totalRows?: number;
  savedCount?: number;
  errorCount?: number;
  errorFileBase64?: string;
  errorFileName?: string;
}

export const useUploadOrderSlipExcel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["uploadOrderSlipExcel"],
    mutationFn: async ({ file, writeId }: { file: File; writeId: string }): Promise<ExcelUploadResponse> => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("writeId", writeId);

      const response = await api.post("/order-slip/upload-excel", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response as unknown as ExcelUploadResponse;
    },
    onSuccess: () => {
      // 성공 시 orderSlip 쿼리 무효화하여 리스트 새로고침
      queryClient.invalidateQueries({ queryKey: [orderSlipQueryKey] });
    },
  });
};

/**
 * 주문 전표 Excel 업로드 양식 다운로드
 */
export const downloadOrderSlipUploadTemplate = async () => {
  try {
    const response = await api.get("/order-slip/upload-template", {
      responseType: "blob",
    });

    const blob = new Blob([response as unknown as BlobPart], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // 파일명에 현재 날짜/시간 추가 (년월일시분 형식)
    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `주문일보_${dateStr}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    const { toast } = await import("sonner");
    toast.success("업로드 양식을 다운로드했습니다.");
  } catch (error) {
    console.error("Failed to download template:", error);
    const { toast } = await import("sonner");
    toast.error("업로드 양식 다운로드에 실패했습니다.");
  }
};
